import { inject } from 'vue';
import type { ApiClient } from '#shopware';
import {
    isDowntimeError,
    SHOPWARE_CIRCUIT_OPEN_ERROR,
    SHOPWARE_TIMEOUT_REASON
} from '../composables/useShopwareDowntime';

/**
 * Registers the shopware downtime hooks centrally on the shared apiClient
 * instance that is provided by @shopware/nuxt-module.
 *
 * Background: the circuit-breaker and downtime tracking must intercept every
 * invoke call, regardless of which composable triggers it. Patching invoke
 * directly on the shared apiClient instance (provided via vueApp.provide) at
 * plugin setup time is the only reliable way to guarantee this – wrapping it
 * inside a composable would only cover call sites that go through that
 * composable, and only when that composable is active.
 *
 * By hooking directly onto the shared vueApp-provided instance here (before any
 * component is mounted) we guarantee that *every* invoke call – regardless of
 * which useShopwareContext version is used – goes through capture/markUp.
 */
export default defineNuxtPlugin({
    name: 'shopware-downtime',
    // @shopware/nuxt-module is a Nuxt module – its plugin runs before app plugins,
    // so vueApp.provide('apiClient', ...) is guaranteed to have happened already.
    setup(nuxtApp) {
        type CircuitState = 'closed' | 'open' | 'half-open';

        const apiClient = nuxtApp.vueApp.runWithContext(() => inject<ApiClient>('apiClient'));

        if (!apiClient) {
            console.warn('[shopware-downtime] apiClient not found – downtime tracking inactive');
            return;
        }

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const anyClient = apiClient as Record<string, any>;

        // Guard: don't register twice
        if (anyClient.__downtimePluginRegistered) return;
        anyClient.__downtimePluginRegistered = true;

        const { markUp, capture } = useShopwareDowntime();

        const INVOKE_TIMEOUT_MS = 5_000;
        const COOLDOWN_MS = 10_000;

        let circuitState: CircuitState = 'closed';
        let cooldownUntil = 0;
        let halfOpenProbeInFlight = false;

        const setOpen = () => {
            circuitState = 'open';
            cooldownUntil = Date.now() + COOLDOWN_MS;
            halfOpenProbeInFlight = false;
        };

        const setClosed = () => {
            circuitState = 'closed';
            cooldownUntil = 0;
            halfOpenProbeInFlight = false;
        };

        const createCircuitOpenError = () => {
            const error = new Error('Shopware temporarily unavailable (circuit open)');
            error.name = SHOPWARE_CIRCUIT_OPEN_ERROR;
            return error;
        };

        const mergeSignals = (signals: Array<AbortSignal | undefined>): AbortSignal | undefined => {
            const activeSignals = signals.filter((signal): signal is AbortSignal => Boolean(signal));

            if (activeSignals.length === 0) return undefined;
            if (activeSignals.length === 1) return activeSignals[0];

            if (typeof AbortSignal.any === 'function') {
                return AbortSignal.any(activeSignals);
            }

            const controller = new AbortController();
            const onAbort = (event: Event) => {
                const signal = event.target as AbortSignal;
                if (!controller.signal.aborted) controller.abort(signal.reason);
            };

            for (const signal of activeSignals) {
                if (signal.aborted) {
                    controller.abort(signal.reason);
                    break;
                }

                signal.addEventListener('abort', onAbort, { once: true });
            }

            return controller.signal;
        };

        if (typeof apiClient.hook === 'function') {
            apiClient.hook('onSuccessResponse', () => markUp());
            apiClient.hook('onResponseError', (error) => capture(error));
        }

        // Wrap invoke as fallback for transport-level errors that never produce
        // an HTTP response (ECONNREFUSED, DNS failure, timeout, …).
        //
        // Circuit-breaker behavior:
        // - closed: normal operation
        // - open: requests fail fast for COOLDOWN_MS
        // - half-open: allow one probe request, then close on success/open on failure
        //
        // This avoids repeated sequential waits (e.g. 3-4 requests x timeout)
        // when Shopware is unresponsive.

        const originalInvoke = anyClient.invoke as ((...args: unknown[]) => Promise<unknown>) | undefined;
        if (typeof originalInvoke === 'function') {
            anyClient.invoke = async function (...args: unknown[]) {
                const now = Date.now();

                if (circuitState === 'open') {
                    if (now < cooldownUntil) {
                        const error = createCircuitOpenError();
                        capture(error);
                        throw error;
                    }

                    circuitState = 'half-open';
                }

                if (circuitState === 'half-open' && halfOpenProbeInFlight) {
                    const error = createCircuitOpenError();
                    capture(error);
                    throw error;
                }

                const isHalfOpenProbe = circuitState === 'half-open';
                if (isHalfOpenProbe) halfOpenProbeInFlight = true;

                const timeoutController = new AbortController();
                const timer = setTimeout(() => timeoutController.abort(SHOPWARE_TIMEOUT_REASON), INVOKE_TIMEOUT_MS);

                // args[1] is the invoke options object { body, headers, pathParams, fetchOptions, … }
                // The signal must go into fetchOptions – that's what invoke spreads into ofetch.raw()
                const opts = (args[1] as Record<string, unknown>) ?? {};
                const fetchOptions = (opts.fetchOptions as Record<string, unknown>) ?? {};
                const callerSignal = fetchOptions.signal as AbortSignal | undefined;
                const signal = mergeSignals([callerSignal, timeoutController.signal]);
                args[1] = { ...opts, fetchOptions: { ...fetchOptions, signal } };

                try {
                    const res = await originalInvoke.apply(this, args);
                    setClosed();
                    markUp();
                    return res;
                } catch (err) {
                    const isTimeoutAbort =
                        timeoutController.signal.aborted && timeoutController.signal.reason === SHOPWARE_TIMEOUT_REASON;

                    if (isTimeoutAbort) {
                        capture({ name: 'AbortError', message: SHOPWARE_TIMEOUT_REASON });
                        setOpen();
                    } else {
                        capture(err);
                        if (isDowntimeError(err)) {
                            setOpen();
                        }
                    }
                    throw err;
                } finally {
                    clearTimeout(timer);
                    if (isHalfOpenProbe) {
                        halfOpenProbeInFlight = false;
                    }
                }
            };
        }
    }
});
